import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, LogIn, Sparkles,
} from 'lucide-react';
import { LegalFooterLinks } from '../components/legal/LegalDocument';
import {
  COST_PILLARS,
  DATA_SOURCES,
  FRAGMENTED_SOURCES,
  INTELLIGENCE_PILLARS,
  PERSONAS,
  PLATFORM_STEPS,
  PRODUCT_PILLARS,
  SCORE_COMPONENTS,
} from '../components/marketing/homeContent';

const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

function SectionLabel({ children }) {
  return (
    <p className="text-emerald-400/90 text-xs font-semibold tracking-[0.22em] uppercase mb-3">
      {children}
    </p>
  );
}

function ScoreBar({ label, value, max }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-slate-300">{label}</span>
        <span className="text-slate-500 tabular-nums">{value}/{max}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-teal-400"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function Home() {
  useEffect(() => {
    const id = 'propscope-home-fonts';
    if (document.getElementById(id)) return undefined;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    document.head.appendChild(link);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen text-slate-200 flex flex-col overflow-x-hidden"
      style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}
    >
      {/* Atmospheric background */}
      <div className="fixed inset-0 -z-10 bg-[#030712]">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(16,185,129,0.18), transparent), radial-gradient(ellipse 60% 40% at 100% 50%, rgba(14,165,233,0.08), transparent)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#030712]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <img src="/propscope-logo.png" alt="PropScope" className="h-11 w-auto" />
            <div className="hidden sm:block">
              <p className="text-white font-semibold leading-tight">PropScope</p>
              <p className="text-slate-500 text-[11px] tracking-wide">UK investment intelligence</p>
            </div>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/features"
              className="hidden sm:inline text-sm text-slate-400 hover:text-white px-3 py-2 transition-colors"
            >
              Features
            </Link>
            <Link
              to="/login"
              className="text-sm text-slate-300 hover:text-white px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
            >
              <LogIn size={15} />
              Sign in
            </Link>
            <Link
              to="/register"
              className="text-sm bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-lg font-semibold inline-flex items-center gap-1.5 transition-colors shadow-lg shadow-emerald-900/30"
            >
              Start free trial
              <ArrowRight size={14} />
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div initial="hidden" animate="visible" variants={fadeUp}>
                <SectionLabel>Property intelligence platform</SectionLabel>
                <h1
                  className="text-[2.6rem] sm:text-5xl lg:text-[3.35rem] text-white leading-[1.08] tracking-tight"
                  style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
                >
                  Turn fragmented property data into{' '}
                  <span className="italic text-emerald-300">clearer, faster</span>{' '}
                  investment decisions.
                </h1>
                <p className="mt-6 text-lg text-slate-400 leading-relaxed max-w-xl">
                  Find the opportunity. Understand the deal. PropScope brings together UK
                  auction stock, agent listings, and open-data intelligence — scored, mapped,
                  and ready for your next move.
                </p>

                <div className="mt-10 flex flex-wrap gap-3">
                  <Link
                    to="/register"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white px-7 py-3.5 rounded-xl font-semibold inline-flex items-center gap-2 transition-all hover:shadow-lg hover:shadow-emerald-900/40"
                  >
                    <Sparkles size={18} />
                    Start 7-day free trial
                    <ArrowRight size={16} />
                  </Link>
                  <Link
                    to="/register?next=/properties"
                    className="border border-slate-700 hover:border-slate-500 bg-slate-900/50 text-white px-7 py-3.5 rounded-xl font-medium inline-flex items-center gap-2 transition-colors"
                  >
                    Create free account
                  </Link>
                </div>

                <p className="mt-6 text-sm text-slate-500 leading-relaxed max-w-lg">
                  Card required for trial — no charge until day 8. Free accounts can explore
                  listings; scores, AI insights, and full neighbourhood reports unlock on Basic
                  or Pro.{' '}
                  <Link to="/features" className="text-emerald-400 hover:underline">
                    See everything included
                  </Link>
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-gradient-to-br from-emerald-500/20 via-transparent to-sky-500/10 rounded-3xl blur-2xl" />
                <div className="relative rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-sm overflow-hidden shadow-2xl">
                  <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-semibold">Deal snapshot</p>
                      <p className="text-slate-500 text-xs">Illustrative — from live platform data</p>
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                      Strong Buy
                    </span>
                  </div>
                  <img
                    src="/property-placeholder.png"
                    alt="Property investment analysis preview"
                    className="w-full h-48 sm:h-56 object-cover opacity-95"
                  />
                  <div className="p-5 grid grid-cols-2 gap-4">
                    <div className="rounded-xl bg-slate-950/60 border border-white/5 p-4">
                      <p className="text-slate-500 text-xs uppercase tracking-wide">Investment score</p>
                      <p className="text-3xl font-bold text-white mt-1 tabular-nums">74</p>
                      <p className="text-emerald-400 text-xs mt-1">Good opportunity</p>
                    </div>
                    <div className="rounded-xl bg-slate-950/60 border border-white/5 p-4">
                      <p className="text-slate-500 text-xs uppercase tracking-wide">Gross yield</p>
                      <p className="text-3xl font-bold text-white mt-1 tabular-nums">6.8%</p>
                      <p className="text-slate-400 text-xs mt-1">vs area avg 5.2%</p>
                    </div>
                    <div className="col-span-2 rounded-xl bg-slate-950/40 border border-white/5 p-4 space-y-3">
                      <p className="text-slate-400 text-xs font-medium">Score breakdown</p>
                      {SCORE_COMPONENTS.map((c) => (
                        <ScoreBar key={c.label} {...c} />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Trust strip */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mt-20 pt-10 border-t border-white/5"
            >
              <p className="text-center text-slate-500 text-xs uppercase tracking-[0.2em] mb-6">
                Powered by trusted UK open data
              </p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
                {DATA_SOURCES.map((src) => (
                  <span key={src} className="text-slate-400 text-sm">{src}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Problem */}
        <section className="px-6 py-20 bg-slate-950/50 border-y border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl">
              <SectionLabel>The real cost</SectionLabel>
              <h2
                className="text-3xl sm:text-4xl text-white leading-tight"
                style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
              >
                More data hasn&apos;t made property investment simpler.
              </h2>
              <p className="mt-4 text-slate-400 text-lg leading-relaxed">
                Finding information isn&apos;t the problem — turning it into a decision quickly is.
                Investors still hunt across portals, auction PDFs, agent emails, and spreadsheets.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-2">
              {FRAGMENTED_SOURCES.map((src) => (
                <span
                  key={src}
                  className="text-sm px-3 py-1.5 rounded-full border border-slate-700/80 bg-slate-900/50 text-slate-400"
                >
                  {src}
                </span>
              ))}
            </div>

            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {COST_PILLARS.map((item, i) => (
                <motion.div
                  key={item.title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/5 bg-slate-900/40 p-6"
                >
                  <item.icon size={22} className="text-amber-400 mb-4" />
                  <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                  <p className="mt-2 text-slate-400 text-sm leading-relaxed">{item.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platform journey */}
        <section className="px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <SectionLabel>How PropScope works</SectionLabel>
              <h2
                className="text-3xl sm:text-4xl text-white"
                style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
              >
                One platform. One view. Better decisions.
              </h2>
              <p className="mt-4 text-slate-400">
                Less time gathering data. More time evaluating opportunities.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {PLATFORM_STEPS.map((step, i) => (
                <motion.div
                  key={step.title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="relative rounded-2xl border border-white/5 bg-gradient-to-b from-slate-900/80 to-slate-950/80 p-6"
                >
                  <span className="text-emerald-500/60 text-xs font-bold tracking-widest">{step.step}</span>
                  <step.icon size={24} className="text-emerald-400 mt-4 mb-3" />
                  <h3 className="text-white font-semibold text-lg">{step.title}</h3>
                  <p className="mt-2 text-slate-400 text-sm leading-relaxed">{step.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Product pillars — PropertyData-style trio */}
        <section className="px-6 py-20 bg-slate-950/40 border-y border-white/5">
          <div className="max-w-6xl mx-auto space-y-8">
            {PRODUCT_PILLARS.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className={`grid lg:grid-cols-2 gap-8 items-center rounded-2xl border border-white/5 bg-gradient-to-br ${pillar.accent} p-8 sm:p-10`}
              >
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <SectionLabel>{pillar.eyebrow}</SectionLabel>
                  <h3
                    className="text-2xl sm:text-3xl text-white leading-snug"
                    style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
                  >
                    {pillar.title}
                  </h3>
                  <p className="mt-4 text-slate-400 leading-relaxed">{pillar.body}</p>
                  <Link
                    to={pillar.to}
                    className="mt-6 inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium text-sm transition-colors"
                  >
                    {pillar.cta}
                    <ArrowRight size={16} />
                  </Link>
                </div>
                <div className={`flex justify-center ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="w-32 h-32 rounded-2xl bg-slate-900/60 border border-white/10 flex items-center justify-center">
                    <pillar.icon size={48} className="text-emerald-400/80" strokeWidth={1.25} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Intelligence grid */}
        <section className="px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <SectionLabel>Intelligence from every angle</SectionLabel>
              <h2
                className="text-3xl sm:text-4xl text-white"
                style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
              >
                One property. A much bigger picture.
              </h2>
              <p className="mt-4 text-slate-400 text-lg">
                A consolidated view of every opportunity — location, market, planning, financials,
                and risk in one place.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {INTELLIGENCE_PILLARS.map((pillar, i) => (
                <motion.div
                  key={pillar.title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/5 bg-slate-900/30 p-6 hover:border-emerald-500/20 transition-colors"
                >
                  <pillar.icon size={20} className="text-emerald-400 mb-3" />
                  <h3 className="text-white font-semibold">{pillar.title}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {pillar.items.map((item) => (
                      <li key={item} className="text-slate-400 text-sm flex items-start gap-2">
                        <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Dual lens — from commercial deck */}
        <section className="px-6 py-20 bg-slate-950/50 border-y border-white/5">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionLabel>Two questions, one platform</SectionLabel>
              <h2
                className="text-3xl sm:text-4xl text-white"
                style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
              >
                A good location doesn&apos;t automatically make a good deal.
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed">
                PropScope separates <strong className="text-slate-300 font-medium">area intelligence</strong>{' '}
                (crime, schools, transport, planning, demographics) from{' '}
                <strong className="text-slate-300 font-medium">deal quality</strong>{' '}
                (price vs market, yield, comparables, AI verdict) — so you can compare opportunities
                and focus on what deserves your attention.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-6">
                <p className="text-slate-500 text-xs uppercase tracking-wide">Area intelligence</p>
                <p className="text-4xl font-bold text-white mt-2 tabular-nums">83<span className="text-lg text-slate-500 font-normal">/100*</span></p>
                <p className="text-slate-400 text-sm mt-3">Schools, connectivity, crime, amenities, local conditions</p>
              </div>
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-950/20 p-6">
                <p className="text-emerald-400/80 text-xs uppercase tracking-wide">Deal quality</p>
                <p className="text-4xl font-bold text-white mt-2 tabular-nums">74<span className="text-lg text-slate-500 font-normal">/100*</span></p>
                <p className="text-slate-400 text-sm mt-3">Price, yield, comparables, planning, property condition</p>
              </div>
              <p className="sm:col-span-2 text-slate-600 text-xs">*Illustrative scores — actual values computed per property in PropScope</p>
            </div>
          </div>
        </section>

        {/* Personas */}
        <section className="px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <SectionLabel>Built for deal professionals</SectionLabel>
              <h2
                className="text-3xl sm:text-4xl text-white"
                style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
              >
                How PropScope helps you
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {PERSONAS.map((p, i) => (
                <motion.div
                  key={p.title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/5 bg-slate-900/30 p-7"
                >
                  <p.icon size={24} className="text-emerald-400 mb-4" />
                  <h3 className="text-white font-semibold text-lg">{p.title}</h3>
                  <p className="mt-3 text-slate-400 text-sm leading-relaxed">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-6 py-20">
          <div className="max-w-4xl mx-auto text-center rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-950 p-10 sm:p-14">
            <h2
              className="text-3xl sm:text-4xl text-white"
              style={{ fontFamily: '"Instrument Serif", Georgia, serif' }}
            >
              Get the insights you need to find your next investment.
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              Try PropScope free for 7 days. Monthly subscription after trial — cancel any time.
              Or create a free account and explore listings today.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/register"
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3.5 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors"
              >
                Start free trial
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                className="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white px-8 py-3.5 rounded-xl font-medium transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8 px-6 space-y-3 bg-[#030712]">
        <LegalFooterLinks />
        <p className="text-center text-slate-600 text-xs">
          © Geekybee Limited 2026 · PropScope — UK Property Investment Intelligence
        </p>
      </footer>
    </div>
  );
}
